import {
	useEffect,
	useMemo,
	useCallback,
	createContext,
	useContext,
} from "react";
import moment from "moment-timezone";
import { useSearchParams } from "react-router-dom";
import { v4 as isUUID } from "is-uuid";
import useSWR from "swr";
import { usePayGroups } from "../../../../utils/api/payGroup";
import {
	getStartDateFromEndDate,
	getEndDateFromStartDate,
} from "../../../Settings/PayPolicies/PayGroup/Form/helpers";
import { useInitialState } from "../../../../utils/hooks/useInitialState";
import { useAllPayGroups } from "../../hooks/useAllPayGroups";

export const DateFiltersContext = createContext();

export const availableModes = ["week", "pay-group"];

const urlDateFormat = "YYYY-MM-DD";

const getQueryParamValue = (name, value) => {
	switch (name) {
		case "from":
		case "to": {
			return moment.parseZone(value).format(urlDateFormat);
		}
		default: {
			return value;
		}
	}
};

export const formatQueryParams = (values, search) => {
	const query = new URLSearchParams(search);
	for (const name in values) {
		const value = values[name];
		if (value === null) {
			query.delete(name);
		} else if (value !== undefined) {
			query.set(name, getQueryParamValue(name, value));
		}
	}

	return query.toString();
};

const filterRegex = /filter\[([^\[\]]*)\]\[([^\[\]]*)\]/;

export const DateFiltersProvider = ({
	initialFilters: _initialFilters,
	children,
}) => {
	const initialFilters = useInitialState(
		_initialFilters || {
			mode: "week",
			from: moment().startOf("week").toISOString(true),
			to: moment().endOf("week").toISOString(true),
			payGroup: undefined,
			payGroupPeriod: undefined,
		},
	);

	const { hasFetchedPayGroups, payGroups } = useAllPayGroups();

	const [search, setSearch] = useSearchParams();

	const urlFilters = useMemo(() => {
		const filters = {
			filters: {},
		};
		for (let [name, value] of search.entries()) {
			if (!name.startsWith("filter")) {
				if (["pageIndex", "pageSize"].includes(name)) {
					filters[name] = Number(value);
				}
				continue;
			}
			const isMulti = name.endsWith("[]");
			const [_, field, method] = filterRegex.exec(name) || [];

			if (!filters.filters[field]) {
				filters.filters[field] = {
					name: field,
					method,
					value: isMulti ? [] : undefined,
				};
			}
			if (isUUID(value)) {
				value = { id: value };
			}
			if (isMulti) {
				if (value) {
					filters.filters[field].value.push(value);
				} else {
					filters.filters[field].value = [];
				}
			} else {
				filters.filters[field].value = value;
			}
		}
		filters.filters = Object.keys(filters.filters).map(
			(name) => filters.filters[name],
		);
		return filters;
	}, [search]);

	const {
		mode,
		from,
		to,
		payGroup: payGroupCode,
		payGroupPeriod,
		filters,
	} = useMemo(() => {
		const mode = search.get("mode");
		const from = search.get("from");
		const to = search.get("to");
		const payGroup = search.get("payGroup");
		const payGroupPeriod = search.get("payGroupPeriod");

		return {
			mode: mode || undefined,
			from: from
				? moment(from, urlDateFormat).toISOString(true)
				: undefined,
			to: to ? moment(to, urlDateFormat).toISOString(true) : undefined,
			payGroup: payGroup || undefined,
			payGroupPeriod: payGroupPeriod || undefined,
			filters: urlFilters,
		};
	}, [search, urlFilters]);

	const payGroup = useMemo(() => {
		return payGroups.find(({ code }) => payGroupCode === code);
	}, [payGroupCode, payGroups]);

	const setFilters = useCallback(
		(filters) => {
			const query = formatQueryParams(filters, search);
			setSearch(query);
		},
		[setSearch, search],
	);

	const resetFilters = useCallback(() => {
		setFilters(initialFilters);
	}, [setFilters, initialFilters]);

	useEffect(() => {
		if (!mode || !availableModes.includes(mode)) {
			resetFilters();
		}
	}, [mode, to, from, resetFilters]);

	useEffect(() => {
		if (mode === "pay-group" && payGroup) {
			if (hasFetchedPayGroups && !payGroup) {
				resetFilters();
			}
		}
	}, [payGroup, payGroups, hasFetchedPayGroups, resetFilters, mode]);

	const { data } = useSWR(() => {
		if (!payGroup) {
			return null;
		}
		return {
			url: `/pay-group/${payGroup.id}/items`,
			params: {
				page: 1,
				perPage: 2,
				"orderBy[lockTimestamp]": "desc",
			},
		};
	});

	useEffect(() => {
		if (mode === "pay-group") {
			if (payGroup && payGroupPeriod) {
				let { frequency, periodStartDate, periodEndDate } = payGroup;
				periodStartDate = moment
					.parseZone(periodStartDate)
					.toISOString(true);
				periodEndDate = moment
					.parseZone(periodEndDate)
					.toISOString(true);
				let period = payGroupPeriod;
				let from, to;
				const result = data?.result;
				
				switch (payGroupPeriod) {
					case "past": {
						if (result?.length === 2) {
							from = moment
								.parseZone(result[1].periodStartDate)
								.toISOString(true);
							to = moment
								.parseZone(result[1].periodEndDate)
								.toISOString(true);
						} else if (result?.length === 1) {
							to = moment
								.parseZone(result[0].periodStartDate)
								.subtract(1, "day")
								.toISOString(true);
							from = getStartDateFromEndDate(
								moment.parseZone(to),
								frequency
							).toISOString(true);
						} else {
							to = moment
								.parseZone(periodStartDate)
								.subtract(1, "day")
								.toISOString(true);
							from = getStartDateFromEndDate(
								moment.parseZone(to),
								frequency,
							).toISOString(true);
						}
						break;
					}
					case "current": {
						if (result?.length > 0) {
							from = moment
								.parseZone(result[0].periodStartDate)
								.toISOString(true);
							to = moment
								.parseZone(result[0].periodEndDate)
								.toISOString(true);
						} else {
							from = periodStartDate;
							to = periodEndDate;
						}
						break;
					}
					case "next": {
						if (result?.length > 1) {
							const end = result[0].periodEndDate;
							from = moment.parseZone(end).add(1, "day").toISOString(true);
							to = getEndDateFromStartDate(
								from,
								frequency,
							).toISOString(true);
						}  else {
							from = moment
								.parseZone(periodEndDate)
								.add(1, "day")
								.toISOString(true);
							to = getEndDateFromStartDate(
								from,
								frequency,
							).toISOString(true);
						}

						break;
					}
					default: {
						period = "current";
						if (result?.length === 2) {
							from = moment
								.parseZone(result[0].periodStartDate)
								.toISOString(true);
							to = moment
								.parseZone(result[0].periodEndDate)
								.toISOString(true);
						} else if (result?.length === 1) {

						} else {
							from = periodStartDate;
							to = periodEndDate;
						}
						break;
					}
				}
				setFilters({ payGroupPeriod: period, from, to });
			}
		} else {
			if (payGroup || payGroupPeriod) {
				setFilters({
					payGroup: null,
					payGroupPeriod: null,
				});
			}
		}
	}, [data?.result, mode, payGroup, payGroupPeriod, setFilters]);

	const value = useMemo(
		() => ({
			mode,
			from,
			to,
			payGroup,
			payGroupPeriod,
			filters,
			setFilters,
		}),
		[mode, from, to, payGroup, payGroupPeriod, filters, setFilters],
	);

	return (
		<DateFiltersContext.Provider value={value}>
			{children}
		</DateFiltersContext.Provider>
	);
};

export const useDateFilters = () => {
	const context = useContext(DateFiltersContext);
	if (context === undefined) {
		throw new Error("useDateFilters should be used within a provider");
	}
	return context;
};
